import { useCallback, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import PaymentVaultABI from 'abis/PaymentVault';
import PayoutExecutorABI from 'abis/PayoutExecutor';
import RevenueSplitterABI from 'abis/RevenueSplitter';
import { ensureCorrectChain } from './vaultChain.utils';

export const isValidVaultAddress = (addr) => /^0x[0-9a-fA-F]{40}$/.test((addr || '').trim());

export const isDaoOrganizerWallet = (walletAddress, organizerAddress) => {
  if (!walletAddress || !organizerAddress) return false;
  return walletAddress.toLowerCase() === String(organizerAddress).trim().toLowerCase();
};

export async function resolveSplitterAddress(vaultAddress, provider) {
  if (!isValidVaultAddress(vaultAddress)) return '';
  const vault = new ethers.Contract(vaultAddress.trim(), PaymentVaultABI, provider);
  const payoutExecAddr = await vault.payoutExecutor();
  const payoutExec = new ethers.Contract(payoutExecAddr, PayoutExecutorABI, provider);
  return payoutExec.splitter();
}

export async function fetchDaoOperators(contract) {
  const list = await contract.getDaoOperators();
  return (list || []).map((a) => ethers.utils.getAddress(a));
}

export function mergeOperatorRows(vaultOperators = [], splitterOperators = []) {
  const map = new Map();
  for (const addr of vaultOperators) {
    const key = addr.toLowerCase();
    map.set(key, { address: addr, onVault: true, onSplitter: false });
  }
  for (const addr of splitterOperators) {
    const key = addr.toLowerCase();
    const row = map.get(key);
    if (row) row.onSplitter = true;
    else map.set(key, { address: addr, onVault: false, onSplitter: true });
  }
  return Array.from(map.values()).sort((a, b) => a.address.localeCompare(b.address));
}

export async function loadVaultRevenueRoles(vaultAddress, chainId, walletAddress) {
  const empty = {
    walletAddress: walletAddress || '',
    splitterAddress: '',
    isVaultOperator: false,
    isSplitterOperator: false,
    isVaultAdmin: false,
    isSplitterAdmin: false,
    operatorRows: [],
  };
  if (!isValidVaultAddress(vaultAddress) || !window.ethereum) return empty;

  await ensureCorrectChain(chainId);
  const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
  const vault = new ethers.Contract(vaultAddress.trim(), PaymentVaultABI, provider);
  const splitterAddress = await resolveSplitterAddress(vaultAddress, provider);
  const splitter = splitterAddress
    ? new ethers.Contract(splitterAddress, RevenueSplitterABI, provider)
    : null;

  const daoOperatorRole = await vault.DAO_OPERATOR_ROLE();
  const adminRole = await vault.ADMIN_ROLE();

  let isVaultOperator = false;
  let isSplitterOperator = false;
  let isVaultAdmin = false;
  let isSplitterAdmin = false;

  if (walletAddress) {
    const w = walletAddress;
    [isVaultOperator, isVaultAdmin] = await Promise.all([
      vault.hasRole(daoOperatorRole, w),
      vault.hasRole(adminRole, w),
    ]);
    if (splitter) {
      const splitterDaoOp = await splitter.DAO_OPERATOR_ROLE();
      const splitterAdmin = await splitter.ADMIN_ROLE();
      [isSplitterOperator, isSplitterAdmin] = await Promise.all([
        splitter.hasRole(splitterDaoOp, w),
        splitter.hasRole(splitterAdmin, w),
      ]);
    }
  }

  const [vaultOps, splitterOps] = await Promise.all([
    fetchDaoOperators(vault),
    splitter ? fetchDaoOperators(splitter) : Promise.resolve([]),
  ]);

  return {
    walletAddress: walletAddress || '',
    splitterAddress,
    isVaultOperator,
    isSplitterOperator,
    isVaultAdmin,
    isSplitterAdmin,
    operatorRows: mergeOperatorRows(vaultOps, splitterOps),
  };
}

const emptyRoles = () => ({
  splitterAddress: '',
  isVaultOperator: false,
  isSplitterOperator: false,
  isVaultAdmin: false,
  isSplitterAdmin: false,
  operatorRows: [],
});

/**
 * On-chain roles for Revenue Items (Vault + RevenueSplitter).
 * Falls back to DAO organizer wallet when vault is not configured (cannot read roles on-chain).
 */
export function useVaultRevenueRoles(vaultAddress, chainId, organizerAddress) {
  const [walletAddress, setWalletAddress] = useState('');
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [roles, setRoles] = useState(emptyRoles());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!window.ethereum) {
      setWalletAddress('');
      setIsOrganizer(false);
      setRoles(emptyRoles());
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const accounts = await provider.listAccounts();
      const account = accounts[0] || '';
      setWalletAddress(account);
      const organizerMatch = isDaoOrganizerWallet(account, organizerAddress);
      setIsOrganizer(organizerMatch);

      if (!isValidVaultAddress(vaultAddress)) {
        setRoles(emptyRoles());
        return;
      }

      const next = await loadVaultRevenueRoles(vaultAddress, chainId, account);
      setRoles(next);
    } catch {
      setRoles(emptyRoles());
    } finally {
      setLoading(false);
    }
  }, [vaultAddress, chainId, organizerAddress]);

  useEffect(() => {
    refresh();
    if (!window.ethereum) return undefined;
    const onChange = () => refresh();
    window.ethereum.on('accountsChanged', onChange);
    window.ethereum.on('chainChanged', onChange);
    return () => {
      window.ethereum.removeListener('accountsChanged', onChange);
      window.ethereum.removeListener('chainChanged', onChange);
    };
  }, [refresh]);

  const hasOnChainAccess =
    roles.isVaultOperator ||
    roles.isSplitterOperator ||
    roles.isVaultAdmin ||
    roles.isSplitterAdmin;

  const canAccessOps = roles.isVaultOperator;
  const canAccessConfiguration = isOrganizer || hasOnChainAccess;

  return {
    walletAddress,
    isOrganizer,
    loading,
    refresh,
    canAccessOps,
    canAccessConfiguration,
    hasOnChainAccess,
    hasVault: isValidVaultAddress(vaultAddress),
    ...roles,
  };
}
